import React, { useState, useMemo, useEffect } from "react"
import {
  Card,
  Select,
  InputNumber,
  Tag,
  Typography,
  Space,
  Divider,
  Row,
  Col,
  Alert,
  Statistic,
  Tooltip,
} from "antd"
import { DatabaseOutlined, EditOutlined, CalculatorOutlined } from "@ant-design/icons"
import { collection, getDocs } from "firebase/firestore"
import { db } from "../../firebase/config"

const { Title, Text } = Typography

// ─────────────────────────────────────────────────────────────────────────────
// REMAINING MOCK DATA (not yet replaced with DB fetches)
// ─────────────────────────────────────────────────────────────────────────────
const MOCK_KNIVES = [
  { code: "K-50x100-2", name: "50×100 mm  2 col", height: 50, rowss: 2, gapRaport: 3 },
  { code: "K-30x80-3", name: "30×80 mm  3 col", height: 30, rowss: 3, gapRaport: 3 },
  { code: "K-70x150-1", name: "70×150 mm  1 col", height: 70, rowss: 1, gapRaport: 3 },
  { code: "New Knife", name: "New Knife (manual)", height: 50, rowss: 1, gapRaport: 3 },
]

const MOCK_VARNISHES = [
  { id: "none", name: "None", xpretp: 0, used: 0 },
  { id: "v1", name: "Gloss UV Varnish", xpretp: 0.8, used: 1 },
  { id: "v2", name: "Matte Varnish", xpretp: 0.6, used: 1 },
]

const MOCK_RELIEFS = [
  { id: "none", name: "None", meters: 0, runningMeters: 0, timePerMin: 0 },
  { id: "r1", name: "Blind Emboss", meters: 20, runningMeters: 30, timePerMin: 45 },
]

const MOCK_FOILS = [
  { id: "none", name: "None", costs: 0 },
  { id: "f1", name: "Gold Hot Foil", costs: 4.5 },
  { id: "f2", name: "Silver Hot Foil", costs: 4.0 },
]

const ADJ_TM = { cleanup: 30, cmyk: 10, pantone: 11 }
const COLOR_CONSUMP = { cmyk: 1.2, pantone: 1.4, varnish: 2.0, gold: 1.5 }
const COLOR_PRICES = { cmyk: 5.0, pantone: 8.0, varnish: 4.0, goldAndSilver: 25.0, coverW: 6.0 }
const FINISHING = {
  m1: { costs: 15, runningMetersMin: 30, timeperminute: 5 },
  m2: { costs: 12, runningMetersMin: 25, timeperminute: 4 },
}
const HOTPRINT_DB = {
  meterPerMinute: 10,
  printCostsPerHour: 45,
  adjustmentTime: 30,
  adjustmentRunningMeters: 50,
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const f2 = (v) =>
  typeof v === "number"
    ? Number(v.toFixed(2)).toLocaleString("en", { minimumFractionDigits: 2 })
    : v
const f4 = (v) => (typeof v === "number" ? Number(v.toFixed(4)).toString() : v)
const fEur = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(/[^0-9.-]/g, ""))
  return `€${Number((isNaN(n) ? 0 : n).toFixed(2)).toLocaleString("en", { minimumFractionDigits: 2 })}`
}

const STEP_COLORS = [
  "#1677ff",
  "#722ed1",
  "#08979c",
  "#d4380d",
  "#389e0d",
  "#0958d9",
  "#5b8c00",
  "#d46b08",
  "#2f54eb",
]

const EMPTY_MACHINE = {
  machine: "",
  speed: 0,
  costs: { electricity: 0, worker: 0, amortization: 0 },
}
const EMPTY_MATERIAL = { id: "", name: "", eurM2: 0, gM2: 0, widths: [] }

// ── Val ───────────────────────────────────────────────────────────────────────
function Val({ label, value, editable, onChange, db, fixed, unit, decimals = 2, width }) {
  const color = db ? "#1677ff" : fixed ? "#888" : "#389e0d"
  const display =
    typeof value === "number"
      ? decimals === 0
        ? Math.round(value)
        : Number(value.toFixed(decimals))
      : value

  return (
    <span
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        margin: "0 5px",
        verticalAlign: "middle",
        gap: 1,
      }}>
      <span
        style={{
          fontSize: 9,
          color,
          fontFamily: "monospace",
          lineHeight: 1,
          whiteSpace: "nowrap",
        }}>
        {label}
      </span>
      {editable ? (
        <InputNumber
          size="small"
          value={value}
          onChange={onChange}
          style={{ width: width || (unit ? 90 : 72), fontSize: 12, fontFamily: "monospace" }}
          addonAfter={unit || undefined}
        />
      ) : (
        <Tag
          color={db ? "blue" : fixed ? "default" : "green"}
          style={{
            margin: 0,
            fontFamily: "monospace",
            fontWeight: 600,
            fontSize: 12,
            lineHeight: "20px",
          }}>
          {display}
          {unit ? ` ${unit}` : ""}
        </Tag>
      )}
    </span>
  )
}

// ── Op ───────────────────────────────────────────────────────────────────────
const Op = ({ s = "×" }) => (
  <span style={{ color: "#aaa", margin: "0 3px", fontSize: 14, userSelect: "none" }}>{s}</span>
)

// ── FL ────────────────────────────────────────────────────────────────────────
function FL({ variable, result, unit, isKey, comment, children, fmt }) {
  const displayed = fmt ? fmt(result) : typeof result === "number" ? f4(result) : result
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 3,
        padding: isKey ? "10px 16px" : "7px 12px",
        background: isKey ? "#f5f0ff" : "#fafafa",
        borderLeft: `3px solid ${isKey ? "#722ed1" : "#e8e8e8"}`,
        borderRadius: 6,
        margin: "3px 0",
        fontFamily: '"JetBrains Mono", "Fira Code", "Courier New", monospace',
      }}>
      <span
        style={{
          fontWeight: 700,
          color: "#1a1a1a",
          fontSize: isKey ? 14 : 12,
          minWidth: 220,
          flexShrink: 0,
        }}>
        {variable}
      </span>
      <span style={{ color: "#bbb", margin: "0 6px", flexShrink: 0 }}>=</span>
      <span style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, flex: 1 }}>
        {children}
      </span>
      <span style={{ color: "#bbb", margin: "0 6px", flexShrink: 0 }}>=</span>
      <span
        style={{
          fontWeight: 700,
          fontSize: isKey ? 16 : 13,
          color: isKey ? "#722ed1" : "#d46b08",
          background: isKey ? "#f0e8ff" : "transparent",
          padding: isKey ? "2px 8px" : 0,
          borderRadius: 4,
          flexShrink: 0,
        }}>
        {displayed}
        {unit && (
          <span style={{ fontSize: 10, fontWeight: 400, color: "#aaa", marginLeft: 4 }}>
            {unit}
          </span>
        )}
      </span>
      {comment && (
        <Tooltip title={comment}>
          <span
            style={{ fontSize: 10, color: "#bbb", marginLeft: 8, cursor: "help", flexShrink: 0 }}>
            // {comment.length > 40 ? comment.slice(0, 40) + "…" : comment}
          </span>
        </Tooltip>
      )}
    </div>
  )
}

// ── SLabel ────────────────────────────────────────────────────────────────────
function SLabel({ icon, text }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 5,
        margin: "14px 0 7px",
        color: "#bbb",
      }}>
      {icon}
      <span
        style={{
          fontSize: 10,
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          fontWeight: 700,
        }}>
        {text}
      </span>
    </div>
  )
}

// ── StepCard ──────────────────────────────────────────────────────────────────
function StepCard({ n, title, color, children, keyResult, keyUnit, disabled }) {
  return (
    <Card
      style={{ marginBottom: 20, borderTop: `4px solid ${color}`, borderRadius: 10 }}
      styles={{
        header: { background: "#fafafa", padding: "10px 20px", borderBottom: "1px solid #f0f0f0" },
      }}
      title={
        <Space size={10}>
          <span
            style={{
              background: color,
              color: "#fff",
              borderRadius: "50%",
              width: 26,
              height: 26,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: 12,
              flexShrink: 0,
            }}>
            {n}
          </span>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{title}</span>
          {keyResult !== undefined && (
            <Tag
              color="purple"
              style={{ marginLeft: 8, fontSize: 13, padding: "1px 10px", fontFamily: "monospace" }}>
              → {typeof keyResult === "number" ? f2(keyResult) : keyResult}
              {keyUnit ? ` ${keyUnit}` : ""}
            </Tag>
          )}
          {disabled && <Tag color="default">disabled</Tag>}
        </Space>
      }>
      <div style={{ display: "flex", flexDirection: "column" }}>{children}</div>
    </Card>
  )
}

// ── DbRow ─────────────────────────────────────────────────────────────────────
function DbRow({ items }) {
  return (
    <Row gutter={[12, 8]} style={{ marginBottom: 4 }}>
      {items.map(({ label, value }) => (
        <Col key={label} xs={24} sm={12} md={8} lg={6}>
          <Space size={4} wrap>
            <Text style={{ fontFamily: "monospace", fontSize: 10, color: "#1677ff" }}>{label}</Text>
            <Tag color="blue" style={{ fontFamily: "monospace", fontWeight: 600, fontSize: 11 }}>
              {value}
            </Tag>
          </Space>
        </Col>
      ))}
    </Row>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
export default function DevSpecCalculations() {
  // ── Selections ──────────────────────────────────────────────────────────────
  const [materialId, setMaterialId] = useState(null)
  const [knifeCode, setKnifeCode] = useState("K-50x100-2")
  const [machineName, setMachineName] = useState(null)
  const [varnishId, setVarnishId] = useState("none")
  const [reliefId, setReliefId] = useState("none")
  const [foilId, setFoilId] = useState("none")

  // ── Firebase: Machines ──────────────────────────────────────────────────────
  const [machines, setMachines] = useState([])
  const [machinesLoading, setMachinesLoading] = useState(true)

  useEffect(() => {
    const fetchMachines = async () => {
      try {
        const snapshot = await getDocs(collection(db, "machines"))
        const data = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => (a.key ?? 0) - (b.key ?? 0))
        setMachines(data)
        if (data.length > 0) setMachineName(data[0].machine)
      } catch (err) {
        console.error("Failed to fetch machines:", err)
      } finally {
        setMachinesLoading(false)
      }
    }
    fetchMachines()
  }, [])

  // ── Firebase: Materials ─────────────────────────────────────────────────────
  const [materials, setMaterials] = useState([])
  const [materialsLoading, setMaterialsLoading] = useState(true)

  useEffect(() => {
    const fetchMaterials = async () => {
      try {
        const snapshot = await getDocs(collection(db, "materials"))
        const data = snapshot.docs
          .map((doc) => {
            const d = doc.data()
            return {
              id: doc.id,
              name: d.material ?? "",
              eurM2: d.eurM2 ?? 0,
              gM2: d.gM2 ?? 0,
              stock: d.stock ?? false,
              widths: Array.isArray(d.widths) ? [...d.widths].sort((a, b) => a - b) : [],
              comment: d.comment ?? "",
            }
          })
          .filter((m) => m.stock) // stock = true only
          .sort((a, b) => a.name.localeCompare(b.name))

        setMaterials(data)

        if (data.length > 0) {
          setMaterialId(data[0].id)
          if (data[0].widths.length > 0) setFormatWidth(data[0].widths[0])
        }
      } catch (err) {
        console.error("Failed to fetch materials:", err)
      } finally {
        setMaterialsLoading(false)
      }
    }
    fetchMaterials()
  }, [])

  // ── Order inputs ────────────────────────────────────────────────────────────
  const [totalCount, setTotalCount] = useState(10000)
  const [formatWidth, setFormatWidth] = useState(100)

  // ── Print colors ────────────────────────────────────────────────────────────
  const [cmykColors, setCmykColors] = useState(4)
  const [pantoneColors, setPantoneColors] = useState(0)
  const [pantonePercent, setPantonePercent] = useState(1.0)
  const [coveringWCount, setCoveringWCount] = useState(0)
  const [coveringWPercent, setCoveringWPercent] = useState(1.0)
  const [goldOrSilver, setGoldOrSilver] = useState(0)
  const [goldOrSilverPct, setGoldOrSilverPct] = useState(1.0)
  const [rastra, setRastra] = useState(1.0)

  // ── Production settings ─────────────────────────────────────────────────────
  const [howManyTimes, setHowManyTimes] = useState(1)
  const [seqProd, setSeqProd] = useState(1)

  // ── Foil inputs ─────────────────────────────────────────────────────────────
  const [foilCount, setFoilCount] = useState(0)
  const [foilPercent, setFoilPercent] = useState(0.0)

  // ── Profit & transport ──────────────────────────────────────────────────────
  const [clientsProfit, setClientsProfit] = useState(20)
  const [extraProfit, setExtraProfit] = useState(0)
  const [userExtraPercent] = useState(0)
  const [transportRate, setTransportRate] = useState(0.5)

  // ── Material change handler — resets formatWidth to first available width ───
  const handleMaterialChange = (id) => {
    setMaterialId(id)
    const selected = materials.find((m) => m.id === id)
    if (selected?.widths?.length > 0) setFormatWidth(selected.widths[0])
  }

  // ── DB look-ups ─────────────────────────────────────────────────────────────
  const mat = useMemo(
    () => materials.find((m) => m.id === materialId) || materials[0] || EMPTY_MATERIAL,
    [materialId, materials],
  )
  const knife = useMemo(
    () => MOCK_KNIVES.find((k) => k.code === knifeCode) || MOCK_KNIVES[0],
    [knifeCode],
  )
  const machine = useMemo(
    () => machines.find((m) => m.machine === machineName) || machines[0] || EMPTY_MACHINE,
    [machineName, machines],
  )
  const varnish = useMemo(
    () => MOCK_VARNISHES.find((v) => v.id === varnishId) || MOCK_VARNISHES[0],
    [varnishId],
  )
  const relief = useMemo(
    () => MOCK_RELIEFS.find((r) => r.id === reliefId) || MOCK_RELIEFS[0],
    [reliefId],
  )
  const foil = useMemo(() => MOCK_FOILS.find((f) => f.id === foilId) || MOCK_FOILS[0], [foilId])

  const labelHeight = knife.height
  const numColumns = knife.rowss
  const gapRaport = knife.gapRaport
  const machineCosts = machine.costs || { electricity: 0, worker: 0, amortization: 0 }

  // ── ALL CALCULATIONS ────────────────────────────────────────────────────────
  const c = useMemo(() => {
    // STEP 1
    const rinduSkaits = 1
    const skIzklajuma = numColumns * rinduSkaits
    const formataAugstums = labelHeight + gapRaport
    const tirieM2 = (((formataAugstums * formatWidth) / 1_000_000) * totalCount) / skIzklajuma
    const tirieTekosieMetri = (tirieM2 / formatWidth) * 1000

    // STEP 2
    const pieregulesanasTM =
      ADJ_TM.cleanup +
      ADJ_TM.cmyk * cmykColors +
      pantoneColors * ADJ_TM.pantone +
      coveringWCount * ADJ_TM.pantone +
      goldOrSilver * ADJ_TM.pantone +
      (tirieTekosieMetri > 2000 ? tirieTekosieMetri * 0.05 : tirieTekosieMetri * 0.1) +
      (howManyTimes - 1) * 80 +
      (seqProd - 1) * 350

    const tehnologiskieZudumiM2 = (pieregulesanasTM * formatWidth) / 1000
    const krasuSk = cmykColors + pantoneColors + goldOrSilver + coveringWCount

    const pieregulesanasLaiksMin = Math.round(
      cmykColors * 10 +
        pantoneColors * 11 +
        coveringWCount * 11 +
        goldOrSilver * 10 +
        20 -
        (krasuSk > 6 ? (krasuSk - 6) * 20 * 0.4 : 0) +
        (howManyTimes - 1) * 60 +
        (seqProd - 1) *
          ((cmykColors * 10 + pantoneColors * 11 + coveringWCount * 11 + goldOrSilver * 10) * 0.6 -
            (krasuSk > 6 ? (krasuSk - 6) * 20 * 0.4 : 0)),
    )

    const razosanasLaiksMin = machine.speed > 0 ? tirieTekosieMetri / machine.speed : 0

    // STEP 3
    const vx = varnish.xpretp
    const vu = varnish.used
    let krasasPaterinsGM2 =
      cmykColors * COLOR_CONSUMP.cmyk +
      pantonePercent * (pantoneColors === 0 ? 0 : 1) * COLOR_CONSUMP.pantone +
      (vx === 0 ? 0 : pantonePercent * vx * COLOR_CONSUMP.varnish) +
      goldOrSilverPct * goldOrSilver * COLOR_CONSUMP.gold +
      (coveringWCount === 0 ? 0 : coveringWPercent * coveringWCount) * rastra
    krasasPaterinsGM2 = Number(krasasPaterinsGM2.toFixed(2))

    const vidCenaKrasaiEurKg =
      krasasPaterinsGM2 === 0
        ? 0
        : Number(
            (
              ((1000 / krasasPaterinsGM2) *
                (cmykColors * COLOR_CONSUMP.cmyk * COLOR_PRICES.cmyk +
                  pantonePercent *
                    (pantoneColors === 0 ? 0 : 1) *
                    COLOR_CONSUMP.pantone *
                    COLOR_PRICES.pantone +
                  (vu === 0
                    ? 0
                    : pantonePercent * vx * COLOR_CONSUMP.varnish * COLOR_PRICES.varnish) +
                  goldOrSilverPct * goldOrSilver * COLOR_CONSUMP.gold * COLOR_PRICES.goldAndSilver +
                  (coveringWCount === 0 ? 0 : coveringWPercent * COLOR_PRICES.coverW) *
                    coveringWCount *
                    rastra)) /
              1000
            ).toFixed(2),
          )

    const krasasPaterinsEur =
      krasasPaterinsGM2 === 0
        ? 0
        : Number(
            (
              (((tirieM2 + tehnologiskieZudumiM2 * 0.5) * krasasPaterinsGM2) / 1000) *
              vidCenaKrasaiEurKg
            ).toFixed(2),
          )

    // STEP 4
    const mc = machine.costs || { electricity: 0, worker: 0, amortization: 0 }
    const elektriba = Number(
      (((pieregulesanasLaiksMin + razosanasLaiksMin) / 60) * 15 * 0.34).toFixed(2),
    )
    const visparejasIzmaksas = Number(
      (
        ((mc.electricity + mc.worker + mc.amortization) *
          (pieregulesanasLaiksMin + razosanasLaiksMin)) /
        60
      ).toFixed(2),
    )
    const materialaIzmaksasEur = Number(
      ((tirieM2 + tehnologiskieZudumiM2) * mat.eurM2 + krasasPaterinsEur).toFixed(2),
    )
    const VC = Number(
      ((materialaIzmaksasEur + visparejasIzmaksas) * (1 + clientsProfit / 100) * 0.07).toFixed(2),
    )
    const razosanasIzmaksas = materialaIzmaksasEur + visparejasIzmaksas + VC + elektriba

    // STEP 5 — uses real gM2 from the material document
    const tiraisSvarsKg = Number(
      (((mat.gM2 + krasasPaterinsGM2 * 0.3) / 1000) * tirieM2).toFixed(2),
    )
    const transports = transportRate * tiraisSvarsKg

    // STEP 6
    const reliefActive = relief.id !== "none"
    const reliefAdjArea = (relief.runningMeters * formatWidth) / 1000
    const productionMatCosts = reliefActive ? mat.eurM2 * (reliefAdjArea + tirieM2) : 0
    const reliefProdTime = relief.runningMeters === 0 ? 0 : tirieTekosieMetri / relief.meters
    const reliefOpCost = (relief.timePerMin + reliefProdTime) / 60
    const reliefElec = Number(((relief.timePerMin / 60) * 20 * 0.15).toFixed(2))

    // STEP 7
    const fm1ProdTime = tirieTekosieMetri / FINISHING.m1.runningMetersMin
    const fm2ProdTime = tirieTekosieMetri / FINISHING.m2.runningMetersMin
    const fm1Elec = (FINISHING.m1.timeperminute / 60) * 15 * 0.12
    const fm2Elec = (FINISHING.m2.timeperminute / 60) * 15 * 0.12
    const fm1Op = (FINISHING.m1.costs * (FINISHING.m1.timeperminute + fm1ProdTime)) / 60
    const fm2Op = (FINISHING.m2.costs * (FINISHING.m2.timeperminute + fm2ProdTime)) / 60

    // STEP 8
    const hp = HOTPRINT_DB
    let hotprintProdTime = 0,
      hotprintOp = 0,
      hotprintElec = 0
    let hotprintAdjArea = 0,
      hotprintMatCost = 0,
      hotprintCost = 0
    if (foilCount > 0) {
      hotprintProdTime = Math.floor(tirieTekosieMetri / hp.meterPerMinute)
      hotprintOp = (hp.printCostsPerHour * (hp.adjustmentTime + hotprintProdTime)) / 60
      hotprintElec = (hp.adjustmentTime / 60) * 20 * 0.15
      hotprintAdjArea = (hp.adjustmentRunningMeters * formatWidth) / 1000
      hotprintMatCost = foil.costs * (hotprintAdjArea + tirieM2) * foilPercent
      hotprintCost = hotprintMatCost + hotprintElec + hotprintOp
    }

    // STEP 9
    const kopejasIzmaksas =
      razosanasIzmaksas +
      reliefOpCost +
      productionMatCosts +
      reliefElec +
      fm1Op +
      fm2Op +
      fm1Elec +
      fm2Elec +
      hotprintCost +
      transports

    const pardosanasSummaEur =
      kopejasIzmaksas * (1 + clientsProfit / 100 + extraProfit / 100 + userExtraPercent / 100)
    const cenaPar1000 = Number(((pardosanasSummaEur / totalCount) * 1000).toFixed(2))
    const costPer1000 = Number(((kopejasIzmaksas / totalCount) * 1000).toFixed(2))
    const papiraPaterins = Number((tirieM2 + tehnologiskieZudumiM2).toFixed(2))

    return {
      rinduSkaits,
      skIzklajuma,
      formataAugstums,
      tirieM2,
      tirieTekosieMetri,
      pieregulesanasTM,
      tehnologiskieZudumiM2,
      krasuSk,
      pieregulesanasLaiksMin,
      razosanasLaiksMin,
      krasasPaterinsGM2,
      vidCenaKrasaiEurKg,
      krasasPaterinsEur,
      elektriba,
      visparejasIzmaksas,
      materialaIzmaksasEur,
      VC,
      razosanasIzmaksas,
      tiraisSvarsKg,
      transports,
      reliefAdjArea,
      productionMatCosts,
      reliefProdTime,
      reliefOpCost,
      reliefElec,
      fm1ProdTime,
      fm2ProdTime,
      fm1Elec,
      fm2Elec,
      fm1Op,
      fm2Op,
      hotprintProdTime,
      hotprintOp,
      hotprintElec,
      hotprintAdjArea,
      hotprintMatCost,
      hotprintCost,
      kopejasIzmaksas,
      pardosanasSummaEur,
      cenaPar1000,
      costPer1000,
      papiraPaterins,
    }
  }, [
    mat,
    machine,
    varnish,
    relief,
    foil,
    labelHeight,
    numColumns,
    gapRaport,
    formatWidth,
    totalCount,
    cmykColors,
    pantoneColors,
    pantonePercent,
    coveringWCount,
    coveringWPercent,
    goldOrSilver,
    goldOrSilverPct,
    rastra,
    howManyTimes,
    seqProd,
    foilCount,
    foilPercent,
    clientsProfit,
    extraProfit,
    userExtraPercent,
    transportRate,
  ])

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        maxWidth: 1020,
        margin: "0 auto",
        padding: "24px 16px",
        fontFamily: "system-ui, sans-serif",
      }}>
      {/* ── PAGE HEADER ────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>
          Label Cost Calculator
        </Title>
        <Text type="secondary">Live step-by-step breakdown — all values update instantly</Text>
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          <Tag color="green">
            <EditOutlined /> editable by you
          </Tag>
          <Tag color="blue">
            <DatabaseOutlined /> filled from database
          </Tag>
          <Tag
            color="orange"
            style={{ color: "#d46b08", borderColor: "#ffd591", background: "#fff7e6" }}>
            calculated value
          </Tag>
          <Tag color="purple">★ key step output</Tag>
        </div>
      </div>

      {/* ── RESULT BANNER ──────────────────────────────────────────────── */}
      <div
        style={{
          background: "linear-gradient(135deg,#e6f4ff,#f0f5ff)",
          border: "1.5px solid #91caff",
          borderRadius: 10,
          padding: "16px 24px",
          marginBottom: 24,
          display: "flex",
          gap: 32,
          flexWrap: "wrap",
        }}>
        <Statistic
          title="Price per 1,000 labels (selling)"
          value={c.cenaPar1000}
          prefix="€"
          precision={2}
          valueStyle={{ color: "#2f54eb", fontSize: 30, fontWeight: 700 }}
        />
        <Statistic
          title="Cost per 1,000 (no markup)"
          value={c.costPer1000}
          prefix="€"
          precision={2}
          valueStyle={{ color: "#d46b08", fontSize: 22 }}
        />
        <Statistic title="Paper consumption" value={c.papiraPaterins} suffix=" m²" precision={2} />
        <Statistic title="Product weight" value={c.tiraisSvarsKg} suffix=" kg" precision={2} />
        <Statistic
          title="Total selling price"
          value={c.pardosanasSummaEur}
          prefix="€"
          precision={2}
        />
      </div>

      {/* ── DATABASE & ORDER CONFIG ────────────────────────────────────── */}
      <Card
        title={
          <>
            <DatabaseOutlined /> &nbsp;Database Selections & Order Configuration
          </>
        }
        style={{ marginBottom: 24, borderTop: "4px solid #1677ff" }}
        styles={{ header: { background: "#fafafa" } }}>
        {/* Total labels / Format width / Profit */}
        <Row gutter={[20, 16]}>
          <Col xs={24} sm={8}>
            <Text strong style={{ display: "block", marginBottom: 4 }}>
              Total Labels
            </Text>
            <InputNumber
              value={totalCount}
              onChange={setTotalCount}
              min={1}
              style={{ width: "100%" }}
              size="large"
              addonAfter="pcs"
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(v) => v.replace(/,/g, "")}
            />
          </Col>

          {/* Format width — Select locked to material widths, InputNumber fallback */}
          <Col xs={24} sm={8}>
            <Text strong style={{ display: "block", marginBottom: 4 }}>
              Format Width
              {mat.widths.length > 0 && (
                <Tag color="blue" style={{ marginLeft: 8, fontSize: 10 }}>
                  locked to material
                </Tag>
              )}
            </Text>
            {mat.widths.length > 0 ? (
              <Select
                value={formatWidth}
                onChange={setFormatWidth}
                style={{ width: "100%" }}
                size="large">
                {mat.widths.map((w) => (
                  <Select.Option key={w} value={w}>
                    {w} mm
                  </Select.Option>
                ))}
              </Select>
            ) : (
              <InputNumber
                value={formatWidth}
                onChange={setFormatWidth}
                min={1}
                style={{ width: "100%" }}
                size="large"
                addonAfter="mm"
              />
            )}
          </Col>

          <Col xs={24} sm={8}>
            <Text strong style={{ display: "block", marginBottom: 4 }}>
              Client Profit Margin
            </Text>
            <InputNumber
              value={clientsProfit}
              onChange={setClientsProfit}
              min={0}
              style={{ width: "100%" }}
              size="large"
              addonAfter="%"
            />
          </Col>
        </Row>

        <Divider style={{ margin: "16px 0 12px" }}>
          <Text type="secondary" style={{ fontSize: 11, letterSpacing: "0.08em" }}>
            DATABASE DROPDOWNS — selecting autofills related formula values
          </Text>
        </Divider>

        <Row gutter={[20, 16]}>
          {/* ── Material (Firebase) ── */}
          <Col xs={24} sm={12} md={8}>
            <Text strong style={{ display: "block", marginBottom: 2 }}>
              Material
            </Text>
            <Select
              value={materialId}
              onChange={handleMaterialChange}
              loading={materialsLoading}
              disabled={materialsLoading}
              showSearch
              optionFilterProp="children"
              style={{ width: "100%", marginBottom: 4 }}
              placeholder={materialsLoading ? "Loading…" : "Select material"}>
              {materials.map((m) => (
                <Select.Option key={m.id} value={m.id}>
                  {m.name}
                </Select.Option>
              ))}
            </Select>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {materialsLoading
                ? "Loading materials…"
                : mat.name
                  ? `€${mat.eurM2}/m²  ·  ${mat.gM2} g/m²  ·  widths: ${mat.widths.join(", ")} mm`
                  : "No material selected"}
            </Text>
            <div style={{ marginTop: 3, display: "flex", flexWrap: "wrap", gap: 3 }}>
              {["materialEurM2", "materialGM2"].map((f) => (
                <Tag key={f} color="blue" style={{ fontSize: 9, margin: 0 }}>
                  {f}
                </Tag>
              ))}
            </div>
          </Col>

          {/* ── Knife / Die (mock) ── */}
          <Col xs={24} sm={12} md={8}>
            <Text strong style={{ display: "block", marginBottom: 2 }}>
              Knife / Die
            </Text>
            <Select
              value={knifeCode}
              onChange={setKnifeCode}
              style={{ width: "100%", marginBottom: 4 }}>
              {MOCK_KNIVES.map((k) => (
                <Select.Option key={k.code} value={k.code}>
                  {k.name}
                </Select.Option>
              ))}
            </Select>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {knife.height}mm H · {knife.rowss} col · {knife.gapRaport}mm gap
            </Text>
            <div style={{ marginTop: 3, display: "flex", flexWrap: "wrap", gap: 3 }}>
              {["labelHeight", "numberOfColumns", "gapRaport"].map((f) => (
                <Tag key={f} color="blue" style={{ fontSize: 9, margin: 0 }}>
                  {f}
                </Tag>
              ))}
            </div>
          </Col>

          {/* ── Machine (Firebase) ── */}
          <Col xs={24} sm={12} md={8}>
            <Text strong style={{ display: "block", marginBottom: 2 }}>
              Machine
            </Text>
            <Select
              value={machineName}
              onChange={setMachineName}
              loading={machinesLoading}
              disabled={machinesLoading}
              style={{ width: "100%", marginBottom: 4 }}
              placeholder={machinesLoading ? "Loading…" : undefined}>
              {machines.map((m) => (
                <Select.Option key={m.machine} value={m.machine}>
                  {m.machine}
                </Select.Option>
              ))}
            </Select>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {machinesLoading
                ? "Loading machines…"
                : machine.machine
                  ? `${machine.speed} m/min  ·  €${machineCosts.electricity + machineCosts.worker + machineCosts.amortization}/h total`
                  : "No machine selected"}
            </Text>
            <div style={{ marginTop: 3, display: "flex", flexWrap: "wrap", gap: 3 }}>
              {["machineSpeed", "machineElectricity", "machineWorker", "machineAmortization"].map(
                (f) => (
                  <Tag key={f} color="blue" style={{ fontSize: 9, margin: 0 }}>
                    {f}
                  </Tag>
                ),
              )}
            </div>
          </Col>

          {/* ── Varnish (mock) ── */}
          <Col xs={24} sm={12} md={8}>
            <Text strong style={{ display: "block", marginBottom: 2 }}>
              Varnish
            </Text>
            <Select
              value={varnishId}
              onChange={setVarnishId}
              style={{ width: "100%", marginBottom: 4 }}>
              {MOCK_VARNISHES.map((v) => (
                <Select.Option key={v.id} value={v.id}>
                  {v.name}
                </Select.Option>
              ))}
            </Select>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {varnish.id !== "none" ? `xpretp: ${varnish.xpretp}` : "No varnish active"}
            </Text>
            <div style={{ marginTop: 3, display: "flex", flexWrap: "wrap", gap: 3 }}>
              {["varnishXpretp", "varnishUse"].map((f) => (
                <Tag key={f} color="blue" style={{ fontSize: 9, margin: 0 }}>
                  {f}
                </Tag>
              ))}
            </div>
          </Col>

          {/* ── Relief (mock) ── */}
          <Col xs={24} sm={12} md={8}>
            <Text strong style={{ display: "block", marginBottom: 2 }}>
              Relief
            </Text>
            <Select
              value={reliefId}
              onChange={setReliefId}
              style={{ width: "100%", marginBottom: 4 }}>
              {MOCK_RELIEFS.map((r) => (
                <Select.Option key={r.id} value={r.id}>
                  {r.name}
                </Select.Option>
              ))}
            </Select>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {relief.id !== "none"
                ? `${relief.meters}m/min  ·  adj: ${relief.runningMeters}m`
                : "Relief disabled"}
            </Text>
            <div style={{ marginTop: 3, display: "flex", flexWrap: "wrap", gap: 3 }}>
              {["reliefMeters", "reliefRunningMeters", "reliefTimePerMin"].map((f) => (
                <Tag key={f} color="blue" style={{ fontSize: 9, margin: 0 }}>
                  {f}
                </Tag>
              ))}
            </div>
          </Col>

          {/* ── Foil (mock) ── */}
          <Col xs={24} sm={12} md={8}>
            <Text strong style={{ display: "block", marginBottom: 2 }}>
              Foil
            </Text>
            <Select value={foilId} onChange={setFoilId} style={{ width: "100%", marginBottom: 4 }}>
              {MOCK_FOILS.map((f) => (
                <Select.Option key={f.id} value={f.id}>
                  {f.name}
                </Select.Option>
              ))}
            </Select>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {foil.id !== "none" ? `€${foil.costs}/m²` : "No foil"}
            </Text>
            <div style={{ marginTop: 3, display: "flex", flexWrap: "wrap", gap: 3 }}>
              <Tag color="blue" style={{ fontSize: 9, margin: 0 }}>
                foilCosts
              </Tag>
            </div>
          </Col>
        </Row>
      </Card>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* STEP 1 — LAYOUT & AREA                                           */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <StepCard
        n={1}
        title="Layout & Area"
        color={STEP_COLORS[0]}
        keyResult={c.tirieTekosieMetri}
        keyUnit="m (running metres)">
        <SLabel
          icon={<DatabaseOutlined style={{ fontSize: 11 }} />}
          text="values filled from knife selection"
        />
        <DbRow
          items={[
            { label: "numberOfColumns", value: `${numColumns}` },
            { label: "labelHeight", value: `${labelHeight} mm` },
            { label: "gapRaport", value: `${gapRaport} mm` },
          ]}
        />
        <SLabel icon={<CalculatorOutlined style={{ fontSize: 11 }} />} text="formulas" />
        <FL
          variable="rinduSkaits"
          result={c.rinduSkaits}
          unit="rows"
          comment="Always 1 row per layout in this system">
          <Val label="constant" value={1} fixed />
        </FL>
        <FL
          variable="skIzklajuma"
          result={c.skIzklajuma}
          unit="pcs/layout"
          comment="how many labels fit in each 'slot'">
          <Val label="numberOfColumns" value={numColumns} db />
          <Op s="×" />
          <Val label="rinduSkaits" value={c.rinduSkaits} fixed />
        </FL>
        <FL
          variable="formataAugstums"
          result={c.formataAugstums}
          unit="mm"
          comment="total height each label slot takes on the roll">
          <Val label="labelHeight" value={labelHeight} db />
          <Op s="+" />
          <Val label="gapRaport" value={gapRaport} db />
        </FL>
        <FL
          variable="tirieM2"
          result={f4(c.tirieM2)}
          unit="m²"
          isKey
          comment="net label area — excludes setup waste">
          <span style={{ color: "#bbb", fontSize: 12 }}>(</span>
          <Val label="formataAugstums" value={c.formataAugstums} fixed />
          <Op s="×" />
          <Val label="formatWidth" value={formatWidth} fixed unit="mm" />
          <Op s="÷" />
          <Val label="1,000,000" value={1000000} fixed />
          <span style={{ color: "#bbb", fontSize: 12 }}>)</span>
          <Op s="×" />
          <Val
            label="totalCount"
            value={totalCount}
            editable
            onChange={setTotalCount}
            decimals={0}
            unit="pcs"
            width={100}
          />
          <Op s="÷" />
          <Val label="skIzklajuma" value={c.skIzklajuma} fixed />
        </FL>
        <FL
          variable="tirieTekosieMetri"
          result={f4(c.tirieTekosieMetri)}
          unit="m"
          isKey
          comment="linear metres of roll needed — drives all time calculations">
          <span style={{ color: "#bbb", fontSize: 12 }}>(</span>
          <Val label="tirieM2" value={Number(c.tirieM2.toFixed(4))} fixed />
          <Op s="÷" />
          <Val label="formatWidth" value={formatWidth} fixed />
          <span style={{ color: "#bbb", fontSize: 12 }}>) ×</span>
          <Val label="1000" value={1000} fixed />
        </FL>
      </StepCard>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* STEP 2 — SETUP TIMES                                             */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <StepCard
        n={2}
        title="Setup & Adjustment Times"
        color={STEP_COLORS[1]}
        keyResult={c.pieregulesanasLaiksMin}
        keyUnit="min (labour clock)">
        <SLabel
          icon={<DatabaseOutlined style={{ fontSize: 11 }} />}
          text="adjustment times from database"
        />
        <DbRow
          items={[
            { label: "pieregulesanaPiekartosana (base cleanup)", value: `${ADJ_TM.cleanup} min` },
            { label: "pieregulesanaCmykGatavieToni (per CMYK color)", value: `${ADJ_TM.cmyk} min` },
            {
              label: "pieregulesanaPantone (per Pantone / W / Gold)",
              value: `${ADJ_TM.pantone} min`,
            },
          ]}
        />
        <SLabel
          icon={<EditOutlined style={{ fontSize: 11 }} />}
          text="color counts & production settings (you edit these)"
        />
        <Row gutter={[12, 8]} style={{ marginBottom: 8 }}>
          {[
            { label: "cmykColors", value: cmykColors, set: setCmykColors, max: 4 },
            { label: "pantoneColors", value: pantoneColors, set: setPantoneColors, max: 10 },
            { label: "coveringWCount", value: coveringWCount, set: setCoveringWCount, max: 5 },
            { label: "goldOrSilver", value: goldOrSilver, set: setGoldOrSilver, max: 3 },
            { label: "howManyTimes", value: howManyTimes, set: setHowManyTimes, min: 1 },
            {
              label: "secigaRazosana (1=No,2=Yes)",
              value: seqProd,
              set: setSeqProd,
              min: 1,
              max: 2,
            },
          ].map(({ label, value, set, min = 0, max = 10 }) => (
            <Col xs={12} sm={8} md={4} key={label}>
              <div
                style={{ fontSize: 9, color: "#389e0d", fontFamily: "monospace", marginBottom: 2 }}>
                {label}
              </div>
              <InputNumber
                value={value}
                onChange={set}
                min={min}
                max={max}
                size="small"
                style={{ width: "100%" }}
              />
            </Col>
          ))}
        </Row>
        <SLabel icon={<CalculatorOutlined style={{ fontSize: 11 }} />} text="formulas" />
        <FL
          variable="krasuSk"
          result={c.krasuSk}
          comment="if >6 colors, overlapping setup = discount applied">
          <Val
            label="cmykColors"
            value={cmykColors}
            editable
            onChange={setCmykColors}
            decimals={0}
          />
          <Op s="+" />
          <Val
            label="pantoneColors"
            value={pantoneColors}
            editable
            onChange={setPantoneColors}
            decimals={0}
          />
          <Op s="+" />
          <Val
            label="goldOrSilver"
            value={goldOrSilver}
            editable
            onChange={setGoldOrSilver}
            decimals={0}
          />
          <Op s="+" />
          <Val
            label="coveringWCount"
            value={coveringWCount}
            editable
            onChange={setCoveringWCount}
            decimals={0}
          />
        </FL>
        <FL
          variable="pieregulesanasTM"
          result={f2(c.pieregulesanasTM)}
          unit="min"
          comment="machine runs during setup → this meters how much paper is wasted">
          <Val label="base" value={ADJ_TM.cleanup} db />
          <Op s="+" />
          <Val
            label="cmykColors"
            value={cmykColors}
            editable
            onChange={setCmykColors}
            decimals={0}
          />
          <Op s="×" />
          <Val label="cmykSetup" value={ADJ_TM.cmyk} db />
          <Op s="+" />
          <Val
            label="pantoneColors"
            value={pantoneColors}
            editable
            onChange={setPantoneColors}
            decimals={0}
          />
          <Op s="×" />
          <Val label="pantoneSetup" value={ADJ_TM.pantone} db />
          <Op s="+ …" />
          <span style={{ fontSize: 11, color: "#bbb" }}>(W + gold + ramp-up + extra passes)</span>
        </FL>
        <FL
          variable="tehnologiskieZudumiM2"
          result={f4(c.tehnologiskieZudumiM2)}
          unit="m²"
          isKey
          comment="paper consumed while machine calibrates — billed to material cost">
          <Val label="pieregulesanasTM" value={Number(c.pieregulesanasTM.toFixed(2))} fixed />
          <Op s="×" />
          <Val label="formatWidth" value={formatWidth} fixed />
          <Op s="÷" />
          <Val label="1000" value={1000} fixed />
        </FL>
        <FL
          variable="pieregulesanasLaiksMin"
          result={c.pieregulesanasLaiksMin}
          unit="min"
          isKey
          comment="ROUND(…) — labour/electricity clock, different formula from TM above">
          <span style={{ fontSize: 11, color: "#888" }}>
            ROUND({cmykColors}×10 + {pantoneColors}×11 + {coveringWCount}×11 + {goldOrSilver}×10 +
            20 base
            {c.krasuSk > 6 ? ` − ${((c.krasuSk - 6) * 20 * 0.4).toFixed(1)} overlap discount` : ""}
            {howManyTimes > 1 ? ` + ${(howManyTimes - 1) * 60}min extra passes` : ""}
            {seqProd > 1 ? " + sequential bonus" : ""}, 0)
          </span>
        </FL>
        <FL
          variable="razosanasLaiksMin"
          result={f4(c.razosanasLaiksMin)}
          unit="min"
          isKey
          comment="actual printing time">
          <Val label="tirieTekosieMetri" value={Number(c.tirieTekosieMetri.toFixed(2))} fixed />
          <Op s="÷" />
          <Val label="machineSpeed" value={machine.speed} db />
        </FL>
      </StepCard>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* STEP 3 — INK & COLOR COSTS                                       */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <StepCard
        n={3}
        title="Ink & Color Costs"
        color={STEP_COLORS[2]}
        keyResult={c.krasasPaterinsEur}
        keyUnit="EUR">
        <SLabel
          icon={<DatabaseOutlined style={{ fontSize: 11 }} />}
          text="consumption & prices from database"
        />
        <DbRow
          items={[
            { label: "CMYK consump.", value: `${COLOR_CONSUMP.cmyk} g/m²` },
            { label: "Pantone consump.", value: `${COLOR_CONSUMP.pantone} g/m²` },
            { label: "Varnish consump.", value: `${COLOR_CONSUMP.varnish} g/m²` },
            { label: "Gold consump.", value: `${COLOR_CONSUMP.gold} g/m²` },
            { label: "CMYK price", value: `€${COLOR_PRICES.cmyk}/kg` },
            { label: "Pantone price", value: `€${COLOR_PRICES.pantone}/kg` },
            { label: "Varnish price", value: `€${COLOR_PRICES.varnish}/kg` },
            { label: "Gold price", value: `€${COLOR_PRICES.goldAndSilver}/kg` },
            { label: "White price", value: `€${COLOR_PRICES.coverW}/kg` },
            { label: "varnishXpretp", value: varnish.xpretp },
            { label: "varnishUse", value: varnish.used },
          ]}
        />
        <SLabel icon={<EditOutlined style={{ fontSize: 11 }} />} text="coverage factors" />
        <Row gutter={[12, 8]} style={{ marginBottom: 8 }}>
          {[
            { label: "pantonePercent", value: pantonePercent, set: setPantonePercent },
            { label: "coveringWPercent", value: coveringWPercent, set: setCoveringWPercent },
            { label: "goldOrSilverPercent", value: goldOrSilverPct, set: setGoldOrSilverPct },
            { label: "rastra", value: rastra, set: setRastra },
          ].map(({ label, value, set }) => (
            <Col xs={12} sm={6} key={label}>
              <div
                style={{ fontSize: 9, color: "#389e0d", fontFamily: "monospace", marginBottom: 2 }}>
                {label}
              </div>
              <InputNumber
                value={value}
                onChange={set}
                min={0}
                max={1}
                step={0.1}
                size="small"
                style={{ width: "100%" }}
              />
            </Col>
          ))}
        </Row>
        <SLabel icon={<CalculatorOutlined style={{ fontSize: 11 }} />} text="formulas" />
        <FL
          variable="krasasPaterinsGM2"
          result={c.krasasPaterinsGM2}
          unit="g/m²"
          comment="total ink consumed per m² — sum of all active ink types">
          <Val
            label="cmykColors"
            value={cmykColors}
            editable
            onChange={setCmykColors}
            decimals={0}
          />
          <Op s="×" />
          <Val label="CMYK_consump" value={COLOR_CONSUMP.cmyk} db />
          <Op s="+" />
          <Val
            label="pantonePercent"
            value={pantonePercent}
            editable
            onChange={setPantonePercent}
          />
          <Op s="×" />
          <span style={{ fontSize: 11, color: "#aaa" }}>IF(pantone&gt;0,1,0)</span>
          <Op s="×" />
          <Val label="Pantone_consump" value={COLOR_CONSUMP.pantone} db />
          <Op s="+" />
          <span style={{ fontSize: 11, color: "#bbb" }}>varnish + gold + white …</span>
        </FL>
        <FL
          variable="vidCenaKrasaiEurKg"
          result={c.vidCenaKrasaiEurKg}
          unit="EUR/kg"
          comment="(1000 ÷ totalG/m²) × Σ(consumption × price) ÷ 1000 — blended price">
          <span style={{ fontSize: 11, color: "#bbb" }}>
            (1000 ÷ {c.krasasPaterinsGM2}) × Σ(each ink: consumption × price) ÷ 1000
          </span>
        </FL>
        <FL
          variable="krasasPaterinsEur"
          result={c.krasasPaterinsEur}
          unit="EUR"
          isKey
          comment="only half the waste is inked (calibration runs at ~50% coverage)">
          <span style={{ color: "#bbb", fontSize: 12 }}>(</span>
          <Val label="tirieM2" value={Number(c.tirieM2.toFixed(3))} fixed />
          <Op s="+" />
          <Val label="waste" value={Number(c.tehnologiskieZudumiM2.toFixed(3))} fixed />
          <Op s="×" />
          <Val label="0.5" value={0.5} fixed />
          <span style={{ color: "#bbb", fontSize: 12 }}>)</span>
          <Op s="×" />
          <Val label="krasasPaterinsGM2" value={c.krasasPaterinsGM2} fixed />
          <Op s="÷" />
          <Val label="1000" value={1000} fixed />
          <Op s="×" />
          <Val label="vidCenaKrasaiEurKg" value={c.vidCenaKrasaiEurKg} fixed />
        </FL>
      </StepCard>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* STEP 4 — CORE PRODUCTION COSTS                                   */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <StepCard
        n={4}
        title="Core Production Costs"
        color={STEP_COLORS[3]}
        keyResult={c.razosanasIzmaksas}
        keyUnit="EUR">
        <SLabel
          icon={<DatabaseOutlined style={{ fontSize: 11 }} />}
          text="machine rates from database"
        />
        <DbRow
          items={[
            { label: "machineElectricity", value: `€${machineCosts.electricity}/h` },
            { label: "machineWorker", value: `€${machineCosts.worker}/h` },
            { label: "machineAmortization", value: `€${machineCosts.amortization}/h` },
          ]}
        />
        <SLabel icon={<CalculatorOutlined style={{ fontSize: 11 }} />} text="formulas" />
        <FL
          variable="elektriba"
          result={c.elektriba}
          unit="EUR"
          comment="15 kW × 0.34 EUR/kWh — machine electricity">
          <span style={{ color: "#bbb", fontSize: 12 }}>(</span>
          <Val label="pieregulesanasLaiksMin" value={c.pieregulesanasLaiksMin} fixed />
          <Op s="+" />
          <Val label="razosanasLaiksMin" value={Number(c.razosanasLaiksMin.toFixed(2))} fixed />
          <span style={{ color: "#bbb", fontSize: 12 }}>)</span>
          <Op s="÷ 60 ×" />
          <Val label="15 kW" value={15} fixed />
          <Op s="×" />
          <Val label="0.34 EUR/kWh" value={0.34} fixed />
        </FL>
        <FL
          variable="visparejasIzmaksas"
          result={c.visparejasIzmaksas}
          unit="EUR"
          comment="electricity + worker + amortization for the full time on machine">
          <span style={{ color: "#bbb", fontSize: 12 }}>(</span>
          <Val label="elec" value={machineCosts.electricity} db />
          <Op s="+" />
          <Val label="worker" value={machineCosts.worker} db />
          <Op s="+" />
          <Val label="amort" value={machineCosts.amortization} db />
          <span style={{ color: "#bbb", fontSize: 12 }}>)</span>
          <Op s="×" />
          <span style={{ color: "#bbb", fontSize: 12 }}>(</span>
          <Val label="setupMin" value={c.pieregulesanasLaiksMin} fixed />
          <Op s="+" />
          <Val label="prodMin" value={Number(c.razosanasLaiksMin.toFixed(2))} fixed />
          <span style={{ color: "#bbb", fontSize: 12 }}>) ÷ 60</span>
        </FL>
        <FL
          variable="materialaIzmaksasEur"
          result={c.materialaIzmaksasEur}
          unit="EUR"
          comment="full substrate area (labels + waste) × material price + all ink">
          <span style={{ color: "#bbb", fontSize: 12 }}>(</span>
          <Val label="tirieM2" value={Number(c.tirieM2.toFixed(3))} fixed />
          <Op s="+" />
          <Val label="wasteM2" value={Number(c.tehnologiskieZudumiM2.toFixed(3))} fixed />
          <span style={{ color: "#bbb", fontSize: 12 }}>) ×</span>
          <Val label="materialEurM2" value={mat.eurM2} db />
          <Op s="+" />
          <Val label="krasasPaterinsEur" value={c.krasasPaterinsEur} fixed />
        </FL>
        <FL
          variable="VC"
          result={c.VC}
          unit="EUR"
          comment="7% overhead rate, scales with expected profit margin">
          <span style={{ color: "#bbb", fontSize: 12 }}>(</span>
          <Val label="materialaIzmaksasEur" value={c.materialaIzmaksasEur} fixed />
          <Op s="+" />
          <Val label="visparejasIzmaksas" value={c.visparejasIzmaksas} fixed />
          <span style={{ color: "#bbb", fontSize: 12 }}>) × (1 +</span>
          <Val
            label="clientsProfit"
            value={clientsProfit}
            editable
            onChange={setClientsProfit}
            unit="%"
          />
          <span style={{ color: "#bbb", fontSize: 12 }}>÷ 100) × 0.07</span>
        </FL>
        <FL
          variable="razosanasIzmaksas"
          result={c.razosanasIzmaksas}
          unit="EUR"
          isKey
          fmt={fEur}
          comment="total core printing cost — fed into Step 9">
          <Val label="materialaIzmaksasEur" value={c.materialaIzmaksasEur} fixed />
          <Op s="+" />
          <Val label="visparejasIzmaksas" value={c.visparejasIzmaksas} fixed />
          <Op s="+" />
          <Val label="VC" value={c.VC} fixed />
          <Op s="+" />
          <Val label="elektriba" value={c.elektriba} fixed />
        </FL>
      </StepCard>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* STEP 5 — WEIGHT & TRANSPORT                                      */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <StepCard
        n={5}
        title="Weight & Transport"
        color={STEP_COLORS[4]}
        keyResult={c.transports}
        keyUnit="EUR">
        <SLabel icon={<EditOutlined style={{ fontSize: 11 }} />} text="user input" />
        <Space style={{ marginBottom: 8 }}>
          <Text style={{ fontFamily: "monospace", fontSize: 11, color: "#389e0d" }}>
            transportsCena
          </Text>
          <InputNumber
            value={transportRate}
            onChange={setTransportRate}
            min={0}
            step={0.1}
            size="small"
            addonAfter="EUR/kg"
            style={{ width: 160 }}
          />
        </Space>
        <SLabel icon={<CalculatorOutlined style={{ fontSize: 11 }} />} text="formulas" />
        <FL
          variable="tiraisSvarsKg"
          result={c.tiraisSvarsKg}
          unit="kg"
          isKey
          comment="real gM2 from material DB + ink adds 30% of its dry weight">
          <span style={{ color: "#bbb", fontSize: 12 }}>(</span>
          <Val label="materialGM2" value={mat.gM2} db />
          <Op s="+" />
          <Val label="krasasPaterinsGM2" value={c.krasasPaterinsGM2} fixed />
          <Op s="×" />
          <Val label="0.3" value={0.3} fixed />
          <span style={{ color: "#bbb", fontSize: 12 }}>) ÷ 1000 ×</span>
          <Val label="tirieM2" value={Number(c.tirieM2.toFixed(3))} fixed />
        </FL>
        <FL variable="transports" result={f2(c.transports)} unit="EUR" isKey>
          <Val label="transportsCena" value={transportRate} editable onChange={setTransportRate} />
          <Op s="×" />
          <Val label="tiraisSvarsKg" value={c.tiraisSvarsKg} fixed />
        </FL>
      </StepCard>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* STEP 6 — RELIEF                                                  */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <StepCard
        n={6}
        title="Relief / Post-Processing  (optional)"
        color={STEP_COLORS[5]}
        keyResult={c.reliefOpCost + c.productionMatCosts + c.reliefElec}
        keyUnit="EUR"
        disabled={reliefId === "none"}>
        {reliefId === "none" ? (
          <Alert
            showIcon
            type="info"
            message="Select a relief profile from the dropdown above to enable this step."
          />
        ) : (
          <>
            <SLabel
              icon={<DatabaseOutlined style={{ fontSize: 11 }} />}
              text="relief specs from database"
            />
            <DbRow
              items={[
                { label: "reliefMeters", value: `${relief.meters} m/min` },
                { label: "reliefRunningMeters (adj)", value: `${relief.runningMeters} m` },
                { label: "reliefTimePerMin (adj time)", value: `${relief.timePerMin} min` },
              ]}
            />
            <SLabel icon={<CalculatorOutlined style={{ fontSize: 11 }} />} text="formulas" />
            <FL
              variable="pecapstrade1PieregulesanaM2"
              result={f4(c.reliefAdjArea)}
              unit="m²"
              comment="substrate wasted during relief machine setup">
              <Val label="reliefRunningMeters" value={relief.runningMeters} db />
              <Op s="×" />
              <Val label="formatWidth" value={formatWidth} fixed />
              <Op s="÷" />
              <Val label="1000" value={1000} fixed />
            </FL>
            <FL variable="productionMaterialCosts" result={f2(c.productionMatCosts)} unit="EUR">
              <Val label="materialEurM2" value={mat.eurM2} db />
              <Op s="×" />
              <span style={{ color: "#bbb", fontSize: 12 }}>(</span>
              <Val label="adjArea" value={Number(c.reliefAdjArea.toFixed(3))} fixed />
              <Op s="+" />
              <Val label="tirieM2" value={Number(c.tirieM2.toFixed(3))} fixed />
              <span style={{ color: "#bbb", fontSize: 12 }}>)</span>
            </FL>
            <FL variable="pecapstrade1RazosanasLaiksMin" result={f2(c.reliefProdTime)} unit="min">
              <Val label="tirieTekosieMetri" value={Number(c.tirieTekosieMetri.toFixed(2))} fixed />
              <Op s="÷" />
              <Val label="reliefMeters" value={relief.meters} db />
            </FL>
            <FL
              variable="pecapstrade1VisparejasIzmaksas"
              result={f2(c.reliefOpCost)}
              unit="EUR"
              isKey>
              <span style={{ color: "#bbb", fontSize: 12 }}>(</span>
              <Val label="reliefTimePerMin" value={relief.timePerMin} db />
              <Op s="+" />
              <Val label="prodTime" value={Number(c.reliefProdTime.toFixed(2))} fixed />
              <span style={{ color: "#bbb", fontSize: 12 }}>) ÷ 60</span>
            </FL>
            <FL
              variable="pecapstradeElektribaEur"
              result={c.reliefElec}
              unit="EUR"
              comment="20 kW × 0.15 EUR/kWh for adjustment time only">
              <span style={{ color: "#bbb", fontSize: 12 }}>(</span>
              <Val label="reliefTimePerMin" value={relief.timePerMin} db />
              <span style={{ color: "#bbb", fontSize: 12 }}>) ÷ 60 × 20 × 0.15</span>
            </FL>
          </>
        )}
      </StepCard>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* STEP 7 — FINISHING MACHINES                                      */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <StepCard
        n={7}
        title="Packaging / Finishing Machines"
        color={STEP_COLORS[6]}
        keyResult={c.fm1Op + c.fm2Op + c.fm1Elec + c.fm2Elec}
        keyUnit="EUR">
        <SLabel
          icon={<DatabaseOutlined style={{ fontSize: 11 }} />}
          text="finishing machine specs from database"
        />
        <DbRow
          items={[
            { label: "productionCosts (M1)", value: `€${FINISHING.m1.costs}/h` },
            {
              label: "productionRunningMetersMin (M1)",
              value: `${FINISHING.m1.runningMetersMin} m/min`,
            },
            {
              label: "productionTimePerMinute (M1)",
              value: `${FINISHING.m1.timeperminute} min setup`,
            },
            { label: "productionCosts2 (M2)", value: `€${FINISHING.m2.costs}/h` },
            {
              label: "productionRunningMetersMin2 (M2)",
              value: `${FINISHING.m2.runningMetersMin} m/min`,
            },
            {
              label: "productionTimePerMinute2 (M2)",
              value: `${FINISHING.m2.timeperminute} min setup`,
            },
          ]}
        />
        <SLabel
          icon={<CalculatorOutlined style={{ fontSize: 11 }} />}
          text="formulas — same pattern for both machines"
        />
        {[
          { n: 1, d: FINISHING.m1, prodTime: c.fm1ProdTime, elec: c.fm1Elec, op: c.fm1Op },
          { n: 2, d: FINISHING.m2, prodTime: c.fm2ProdTime, elec: c.fm2Elec, op: c.fm2Op },
        ].map(({ n: mn, d, prodTime, elec, op }) => (
          <React.Fragment key={mn}>
            <Divider style={{ margin: "8px 0 4px" }}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Machine {mn}
              </Text>
            </Divider>
            <FL
              variable={`produkcijasKomplektesana${mn > 1 ? mn : ""}RazosanasLaiks`}
              result={f4(prodTime)}
              unit="min">
              <Val label="tirieTekosieMetri" value={Number(c.tirieTekosieMetri.toFixed(2))} fixed />
              <Op s="÷" />
              <Val label={`M${mn}_speed`} value={d.runningMetersMin} db />
            </FL>
            <FL
              variable={`produkcijasKomplektesana${mn > 1 ? mn : ""}ElektribaEur`}
              result={f2(elec)}
              unit="EUR">
              <span style={{ color: "#bbb", fontSize: 12 }}>(</span>
              <Val label={`M${mn}_setup`} value={d.timeperminute} db />
              <span style={{ color: "#bbb", fontSize: 12 }}>) ÷ 60 × 15 × 0.12</span>
            </FL>
            <FL
              variable={`produkcijasKomplektesana${mn > 1 ? mn : ""}VisparejasIzmaksas`}
              result={f2(op)}
              unit="EUR"
              isKey>
              <Val label={`M${mn}_cost`} value={d.costs} db />
              <Op s="×" />
              <span style={{ color: "#bbb", fontSize: 12 }}>(</span>
              <Val label="setup" value={d.timeperminute} db />
              <Op s="+" />
              <Val label="prodTime" value={Number(prodTime.toFixed(2))} fixed />
              <span style={{ color: "#bbb", fontSize: 12 }}>) ÷ 60</span>
            </FL>
          </React.Fragment>
        ))}
      </StepCard>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* STEP 8 — HOTFOIL                                                 */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <StepCard
        n={8}
        title="Hotfoil / Hotprint  (optional)"
        color={STEP_COLORS[7]}
        keyResult={c.hotprintCost}
        keyUnit="EUR"
        disabled={foilId === "none"}>
        {foilId === "none" ? (
          <Alert
            showIcon
            type="info"
            message="Select a foil from the dropdown above, then set foilCount > 0 to enable this step."
          />
        ) : (
          <>
            <SLabel
              icon={<DatabaseOutlined style={{ fontSize: 11 }} />}
              text="hotprint specs from database"
            />
            <DbRow
              items={[
                { label: "hotprintMeterPerMin", value: `${HOTPRINT_DB.meterPerMinute} m/min` },
                { label: "hotprintCostsPerH", value: `€${HOTPRINT_DB.printCostsPerHour}/h` },
                { label: "hotprintAdjustmentTimeMin", value: `${HOTPRINT_DB.adjustmentTime} min` },
                {
                  label: "hotprintAdjustmentRunningMeters",
                  value: `${HOTPRINT_DB.adjustmentRunningMeters} m`,
                },
                { label: "foilCosts", value: `€${foil.costs}/m²` },
              ]}
            />
            <SLabel icon={<EditOutlined style={{ fontSize: 11 }} />} text="user inputs" />
            <Row gutter={12} style={{ marginBottom: 8 }}>
              <Col xs={12} sm={6}>
                <div
                  style={{
                    fontSize: 9,
                    color: "#389e0d",
                    fontFamily: "monospace",
                    marginBottom: 2,
                  }}>
                  foilCount
                </div>
                <InputNumber
                  value={foilCount}
                  onChange={setFoilCount}
                  min={0}
                  size="small"
                  style={{ width: "100%" }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <div
                  style={{
                    fontSize: 9,
                    color: "#389e0d",
                    fontFamily: "monospace",
                    marginBottom: 2,
                  }}>
                  foilPercent (coverage)
                </div>
                <InputNumber
                  value={foilPercent}
                  onChange={setFoilPercent}
                  min={0}
                  max={1}
                  step={0.1}
                  size="small"
                  style={{ width: "100%" }}
                />
              </Col>
            </Row>
            <SLabel icon={<CalculatorOutlined style={{ fontSize: 11 }} />} text="formulas" />
            <FL
              variable="hotprintProductionTimePerMin"
              result={c.hotprintProdTime}
              unit="min"
              comment="INT() — floor, not round">
              <span style={{ color: "#bbb", fontSize: 12 }}>INT(</span>
              <Val label="tirieTekosieMetri" value={Number(c.tirieTekosieMetri.toFixed(2))} fixed />
              <Op s="÷" />
              <Val label="hotprintMeterPerMin" value={HOTPRINT_DB.meterPerMinute} db />
              <span style={{ color: "#bbb", fontSize: 12 }}>)</span>
            </FL>
            <FL variable="hotprintTotalCosts" result={f2(c.hotprintOp)} unit="EUR">
              <Val label="costsPerH" value={HOTPRINT_DB.printCostsPerHour} db />
              <Op s="×" />
              <span style={{ color: "#bbb", fontSize: 12 }}>(</span>
              <Val label="adjTime" value={HOTPRINT_DB.adjustmentTime} db />
              <Op s="+" />
              <Val label="prodTime" value={c.hotprintProdTime} fixed />
              <span style={{ color: "#bbb", fontSize: 12 }}>) ÷ 60</span>
            </FL>
            <FL variable="hotprintAdjustmentM2" result={f4(c.hotprintAdjArea)} unit="m²">
              <Val label="adjMeters" value={HOTPRINT_DB.adjustmentRunningMeters} db />
              <Op s="×" />
              <Val label="formatWidth" value={formatWidth} fixed />
              <Op s="÷ 1000" />
            </FL>
            <FL variable="hotprintMaterialCosts" result={f2(c.hotprintMatCost)} unit="EUR">
              <Val label="foilCosts" value={foil.costs} db />
              <Op s="×" />
              <span style={{ color: "#bbb", fontSize: 12 }}>(</span>
              <Val label="adjArea" value={Number(c.hotprintAdjArea.toFixed(3))} fixed />
              <Op s="+" />
              <Val label="tirieM2" value={Number(c.tirieM2.toFixed(3))} fixed />
              <span style={{ color: "#bbb", fontSize: 12 }}>)</span>
              <Op s="×" />
              <Val label="foilPercent" value={foilPercent} editable onChange={setFoilPercent} />
            </FL>
            <FL variable="hotprintCost" result={f2(c.hotprintCost)} unit="EUR" isKey>
              <Val label="hotprintMatCost" value={Number(c.hotprintMatCost.toFixed(2))} fixed />
              <Op s="+" />
              <Val label="hotprintElec" value={Number(c.hotprintElec.toFixed(2))} fixed />
              <Op s="+" />
              <Val label="hotprintTotalCosts" value={Number(c.hotprintOp.toFixed(2))} fixed />
            </FL>
          </>
        )}
      </StepCard>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* STEP 9 — FINAL ASSEMBLY & PRICE                                  */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <StepCard
        n={9}
        title="Final Assembly & Price"
        color={STEP_COLORS[8]}
        keyResult={c.cenaPar1000}
        keyUnit="EUR / 1,000 labels">
        <SLabel icon={<EditOutlined style={{ fontSize: 11 }} />} text="profit adjustments" />
        <Row gutter={[12, 8]} style={{ marginBottom: 8 }}>
          {[
            { label: "clientsProfit", value: clientsProfit, set: setClientsProfit },
            { label: "extraProfitSlider", value: extraProfit, set: setExtraProfit },
            { label: "userExtraPercent (account)", value: userExtraPercent, set: null },
          ].map(({ label, value, set }) => (
            <Col xs={24} sm={8} key={label}>
              <div
                style={{
                  fontSize: 9,
                  color: set ? "#389e0d" : "#1677ff",
                  fontFamily: "monospace",
                  marginBottom: 2,
                }}>
                {label}
              </div>
              {set ? (
                <InputNumber
                  value={value}
                  onChange={set}
                  min={0}
                  size="small"
                  addonAfter="%"
                  style={{ width: "100%" }}
                />
              ) : (
                <Tag color="blue" style={{ fontFamily: "monospace" }}>
                  {value}%
                </Tag>
              )}
            </Col>
          ))}
        </Row>
        <SLabel
          icon={<CalculatorOutlined style={{ fontSize: 11 }} />}
          text="cost build-up from all steps"
        />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(185px,1fr))",
            gap: 8,
            marginBottom: 12,
          }}>
          {[
            { label: "razosanasIzmaksas (Step 4)", value: c.razosanasIzmaksas },
            {
              label: "relief total (Step 6)",
              value: c.reliefOpCost + c.productionMatCosts + c.reliefElec,
            },
            {
              label: "finishing machines (Step 7)",
              value: c.fm1Op + c.fm2Op + c.fm1Elec + c.fm2Elec,
            },
            { label: "hotprintCost (Step 8)", value: c.hotprintCost },
            { label: "transports (Step 5)", value: c.transports },
          ].map(({ label, value }) => (
            <div
              key={label}
              style={{
                background: "#f9f9f9",
                border: "1px solid #f0f0f0",
                borderRadius: 6,
                padding: "8px 12px",
              }}>
              <div
                style={{ fontFamily: "monospace", fontSize: 10, color: "#888", marginBottom: 4 }}>
                {label}
              </div>
              <div
                style={{
                  fontFamily: "monospace",
                  fontWeight: 700,
                  color: "#d46b08",
                  fontSize: 14,
                }}>
                {fEur(value)}
              </div>
            </div>
          ))}
        </div>
        <FL
          variable="kopejasIzmaksas"
          result={fEur(c.kopejasIzmaksas)}
          unit=""
          isKey
          comment="sum of all cost components above">
          <span style={{ fontSize: 11, color: "#aaa" }}>
            razosanasIzmaksas + relief + finishing + hotfoil + transports
          </span>
        </FL>
        <FL
          variable="pardosanasSummaEur"
          result={fEur(c.pardosanasSummaEur)}
          unit=""
          isKey
          comment="selling price after all profit markups">
          <Val label="kopejasIzmaksas" value={fEur(c.kopejasIzmaksas)} fixed />
          <Op s="×" />
          <span style={{ color: "#bbb", fontSize: 12 }}>(1 +</span>
          <Val
            label="clientsProfit"
            value={clientsProfit}
            editable
            onChange={setClientsProfit}
            unit="%"
          />
          <Op s="÷ 100 +" />
          <Val
            label="extraProfit"
            value={extraProfit}
            editable
            onChange={setExtraProfit}
            unit="%"
          />
          <Op s="÷ 100 +" />
          <Val label="userExtra" value={userExtraPercent} db unit="%" />
          <span style={{ color: "#bbb", fontSize: 12 }}>÷ 100)</span>
        </FL>
        <Divider style={{ margin: "20px 0 16px" }} />
        <div
          style={{
            background: "linear-gradient(135deg,#e6f0ff,#f0f5ff)",
            border: "2px solid #91b4ff",
            borderRadius: 12,
            padding: "20px 28px",
          }}>
          <Row gutter={[32, 16]}>
            <Col xs={24} sm={8}>
              <Statistic
                title="Price per 1,000 labels (selling)"
                value={c.cenaPar1000}
                prefix="€"
                precision={2}
                valueStyle={{ color: "#2f54eb", fontSize: 30, fontWeight: 800 }}
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title="Cost per 1,000 (no markup)"
                value={c.costPer1000}
                prefix="€"
                precision={2}
                valueStyle={{ color: "#d46b08", fontSize: 24 }}
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title="Total order cost"
                value={c.kopejasIzmaksas}
                prefix="€"
                precision={2}
                valueStyle={{ fontSize: 22 }}
              />
            </Col>
          </Row>
        </div>
      </StepCard>
    </div>
  )
}
